import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Download, PlusCircle, Search } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, formatPrice } from "@/lib/api";
import { btnGhost, btnPrimary, inputCls, PageHead, ProductStatus } from "./ui";

const Sel = ({ value, onChange, testId, children }) => (
  <select data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} sm:w-auto`}>{children}</select>
);

export default function AdminProductList() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState(sp.get("q") || "");
  const status = sp.get("status") || "", category = sp.get("category") || "", stock = sp.get("stock") || "";

  useEffect(() => { api.get("/admin/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => {
    const id = setTimeout(() => {
      api.get("/admin/products", { params: { q: q || undefined, status: status || undefined, category: category || undefined, stock: stock || undefined } })
        .then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
    }, 250);
    return () => clearTimeout(id);
  }, [q, status, category, stock]);

  const setParam = (k, v) => { const n = new URLSearchParams(sp); v ? n.set(k, v) : n.delete(k); setSp(n, { replace: true }); };
  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/products/export.csv", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data); a.download = "bdc-prodotti.csv"; a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="admin-products">
      <PageHead title="Prodotti" subtitle={items ? `${items.length} prodotti` : "…"}>
        <button data-testid="products-export-csv" onClick={exportCsv} className={btnGhost}><Download className="w-4 h-4" /> CSV</button>
        <Link to="/admin/prodotti/nuovo" data-testid="products-new-button" className={btnPrimary}><PlusCircle className="w-4 h-4" /> Nuovo</Link>
      </PageHead>
      <div className="flex flex-col sm:flex-row gap-2 mb-6">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#956842]" />
          <input data-testid="products-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, SKU, colore" className={`${inputCls} pl-9`} />
        </div>
        <Sel testId="products-filter-status" value={status} onChange={(v) => setParam("status", v)}>
          <option value="">Tutti gli stati</option><option value="draft">Bozze</option><option value="published">Pubblicati</option><option value="archived">Archiviati</option>
        </Sel>
        <Sel testId="products-filter-category" value={category} onChange={(v) => setParam("category", v)}>
          <option value="">Tutte le categorie</option>{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </Sel>
        <Sel testId="products-filter-stock" value={stock} onChange={(v) => setParam("stock", v)}>
          <option value="">Tutte le scorte</option><option value="unique">Pezzi unici</option><option value="low">Sotto soglia</option><option value="out">Esauriti</option>
        </Sel>
      </div>
      <div className="space-y-2">
        {items?.length === 0 && <p data-testid="products-empty" className="py-12 text-center text-sm text-[#242321]/60">Nessun prodotto trovato.</p>}
        {(items || []).map((p) => (
          <Link key={p.id} to={`/admin/prodotti/${p.id}`} data-testid={`admin-product-row-${p.id}`} className="flex items-center gap-3 sm:gap-4 bg-white/60 border border-[#D5B98C]/50 p-3 hover:border-[#39251D] transition-colors">
            {p.images[0] ? <img src={p.images[0]} alt="" className="w-12 h-14 sm:w-14 sm:h-16 object-cover shrink-0" /> : <div className="w-12 h-14 sm:w-14 sm:h-16 bg-[#E9DFCE] shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium leading-snug line-clamp-2">{p.name}</div>
              <div className="text-xs text-[#242321]/55 truncate">{p.sku} · {p.category_name || "Senza categoria"}</div>
              <div className="sm:hidden mt-1 flex items-center gap-2 text-xs"><span>{formatPrice(p.sale_price ?? p.price)}</span><span>·</span><span>{p.stock === 1 ? "Pezzo unico" : `${p.stock} pz`}</span><ProductStatus status={p.status} /></div>
            </div>
            <div className="hidden sm:block text-sm w-24 text-right">{formatPrice(p.sale_price ?? p.price)}</div>
            <div className="hidden sm:block text-xs w-16 text-right">{p.stock === 1 ? <span className="font-serif italic text-sm text-[#956842]">Unico</span> : `${p.stock} pz`}</div>
            <span className="hidden sm:block"><ProductStatus status={p.status} /></span>
          </Link>
        ))}
      </div>
    </div>
  );
}
